/**
 * The corpus for the Ask panel's empty-state suggested-questions list (VRG-227).
 *
 * Every entry here must be answerable by ONE of the two things Ask Vera can actually do:
 *
 *   1. the single `query` grammar's five metrics — scans, certificates, owners, leads,
 *      transfers — each restricted to its own allow-listed dimensions/aggregates/filters
 *      (see `ALLOWED_METRIC_DIMENSIONS` etc. in `apps/api/src/features/ask/ask.tools.ts`),
 *      or
 *   2. a published, `audience: brand` (or wider) doc guide — the corpus Ask's retrieval
 *      draws from. `audience: internal` guides are never in that corpus for a brand user,
 *      so a question only an internal guide answers is a dead end here even though the
 *      guide exists in the repo.
 *
 * A suggestion that fails teaches a first-time user the feature doesn't work, which is worse
 * than no suggestion — so this list is deliberately NOT "everything the product could
 * plausibly be asked". Notably absent: certificate transfer. All three transfer guides
 * (`gift-and-transfer.md`, `certificate-gift-transfer-and-provenance.md`,
 * `gift-transfer-verify.md`) are `audience: internal`, so "how do I transfer a certificate"
 * has no brand-facing guide behind it today. It goes back on this list the day one of those
 * is published for brands (or a customer-facing transfer guide is written).
 *
 * ── What used to be gapped here is now covered (updated, second VRG-227 pass) ───────────
 * The paragraph above used to say LEADS and TRANSFERS/resale were considered-but-excluded
 * because no tool read them. That is no longer true — `ask.tools.ts`'s single `query`
 * grammar covers all five metrics (scans, certificates, owners, leads, transfers,
 * `ALLOWED_METRIC_DIMENSIONS`), and both are represented below now. What remains genuinely
 * unanswerable, and so still excluded on purpose:
 *   - anything crossing TWO metrics in one question ("which owners who registered this
 *     month also scanned the most" — `query` answers one metric per call, never a join
 *     across metrics).
 *   - certificate transfer HOW-TO — see the transfer-docs note above; still no
 *     `audience: brand` transfer guide to answer it from.
 *
 * ── The permission-gated `owner` scan dimension IS represented here, with a known gap ────
 * `query`'s `dimension: 'owner'` on `metric: 'scans'` (VRG-227) additionally requires
 * `ViewCustomers` on top of the `scans` metric's own `ViewAnalytics` gate. This list has no
 * per-viewer filtering — it is one static array rendered for every brand user regardless of
 * their permission set — so a question like "which owner scans their certificates most
 * often" would silently fail to resolve as asked for anyone lacking `ViewCustomers` (the
 * model simply never sees "owner" as an available dimension value for them, per
 * `dimsForMetric`). That is exactly the "teaches a first-time user the feature doesn't
 * work" failure mode this file exists to avoid, for a meaningful slice of viewers — kept in
 * the corpus anyway (two entries, below) because most brand users DO carry `ViewCustomers`
 * (it also gates the Owners/Leads tabs, so a viewer without it has little else to do with
 * this panel's people-shaped questions either). Compute this corpus per-viewer against their
 * actual permission set to close the gap properly.
 *
 * ── Third pass (VRG-227) — rebalancing toward analytics ─────────────────────────────────
 * The first two passes established coverage (every metric, every dimension, every
 * aggregate, at least once) and a docs corpus. That left the list roughly a third docs
 * questions, which undersells the panel — a docs question is answerable by any search box;
 * an analytics question over THIS brand's own scans/transfers/leads is the thing only this
 * panel can do, and it's the empty state where a first-time user forms their model of what
 * the panel is for. This pass adds ~30 more data questions, deliberately NOT more of the
 * same shapes already covered — it draws from the axes the first two passes left thin:
 * dimension+filter combinations (a ranking narrowed by status/outcome/type, not just a bare
 * ranking or a bare filter), and explicit time windows (this quarter, year to date, the
 * last 30 days) rather than only "this month"/"last week". No new metric, dimension, or
 * filter is invented — every addition is a new COMBINATION of the same allow-listed
 * vocabulary `ask.tools.ts` already exposes.
 *
 * Kept as its own module, not a literal in the panel component, so the component doesn't
 * carry a 100+-line array inline.
 */
export const ASK_SUGGESTED_QUESTIONS: readonly string[] = [
  // Data — query_scans, group_by country/city/product/month/day.
  'Which country are my certificates scanned in most?',
  'Which city has the most scans this month?',
  'Which product gets scanned the most?',
  'Where were my certificates scanned last month?',
  'What does my scan activity look like by day this week?',
  'Which month had the most certificate scans?',
  'Which product gets scanned most in France?',
  'Which city has the most scans in the US?',
  'How has my monthly scan volume changed over time?',
  'Which day had the most scans this month?',

  // Data — query_scans, verification outcome.
  'How many scans failed verification?',
  'What share of my scans failed verification last month?',
  'How many of my scans have been flagged as suspicious?',
  'How many verified scans did I get last week?',

  // Data — scans, additional filter combinations not covered above (country-only totals;
  // a dimension paired with the outcome filter rather than outcome alone).
  'How many scans came from Germany this year?',
  'Which product had the most scans flagged as suspicious?',

  // Data — query_certificates (status / group_by).
  'How many certificates are active right now?',
  'How many certificates do I have in total?',
  'How many certificates are archived?',
  'How many of my certificates are mid-transfer right now?',
  'Which product has the most certificates still inactive?',
  'How many certificates did I create this month?',

  // Data — certificates, remaining status/product coverage: the status dimension itself
  // (not just a status filter), a plain product ranking, two more single-status filters,
  // and includeArchived on a totals query.
  "What's the breakdown of my certificates by status?",
  'Which product has the most certificates overall?',
  'How many of my certificates have been reported lost?',
  'How many of my certificates have been reported stolen?',
  'How many certificates do I have in total, including archived ones?',
  'How many certificates are waiting for the recipient to accept a transfer?',

  // Data — query_owners.
  'How many owners have completed registration?',
  'How many owners do I have?',
  'How many of my owners are currently active?',

  // Data — owners, month/day dimension coverage (date_trunc on owner.created_at), plus a
  // plain date-windowed totals question — none of these existed before.
  'Which month brought in the most new owners?',
  'How many new owners registered each day this week?',
  'Which month had the most owners complete registration?',
  'How many new owners did I gain this month?',

  // Data — transfers. The resale/ownership-change metric, and the ONLY one carrying
  // `sum`/`avg` (ASK_METRICS.transfers.aggregates), so the "what is it worth" questions
  // live here and nowhere else.
  //
  // ⚠️ The price questions below are answerable but carry a live caveat: `transfers.price`
  // sits beside a separate `currency` column, so an average across mixed currencies is
  // arithmetically fine and semantically meaningless. Listed anyway — a brand WILL ask
  // this, and surfacing it here makes the gap visible rather than letting it lurk until
  // someone asks it cold. Tracked as a follow-up on the tool, not on this corpus.
  'Which product holds its value best?',
  "What's the average transfer price by month?",
  'Which country are my products being resold in most?',
  'Which products get transferred the most?',
  'How many transfers completed this month?',
  'How many transfers are still waiting on the buyer?',
  'How has resale activity changed month over month?',

  // Data — transfers, remaining dimension/aggregate coverage: the day and status
  // dimensions (neither used above), aggregate="sum" ranked by product (distinct from
  // "holds its value best", which is aggregate="avg"), aggregate="avg" ranked by country,
  // the transferType filter (gift, not sell), and a plain sum-bearing totals question.
  'Which day had the most transfers?',
  "What's the breakdown of my transfers by status?",
  'Which product has generated the most total resale value?',
  'Which country has the highest average resale price?',
  'How many certificates have I given away as gifts?',
  "What's the total value of everything transferred this year?",

  // Data — leads. Captured emails from scans (the same `notAnOwner` predicate the
  // platform's own Leads tab uses), so these agree with that page by construction.
  // NOTE: `leads` has no `owner` dimension (ALLOWED_METRIC_DIMENSIONS.leads is
  // product/month/day), so "which owner brings in the most leads" is deliberately NOT
  // here — it would be a suggestion the grammar rejects, which is worse than no
  // suggestion at all.
  'Which product is generating the most leads?',
  'How many leads did I capture last month?',
  'How many new leads came in this week?',

  // Data — leads, the month/day dimensions (product was already covered above).
  'Which month generated the most leads?',
  'What day this week brought in the most leads?',

  // Data — scans grouped by owner. Double-gated: needs ViewCustomers on top of the scans
  // metric's own permission, so a caller without it is told what they lack rather than
  // being shown an empty result.
  'Which owner scans their certificates the most?',
  'Which owner scans the most from the US?',

  // Data — scans, third pass: dimension + outcome-filter combinations not covered above
  // (outcome paired with city/product/country rather than left as the sole filter).
  'Which city had the most flagged scans?',
  'Which product had the most verified scans?',
  'Which country has the most invalid scans?',

  // Data — scans, third pass: explicit time windows on a totals/ranked query, distinct
  // from the "this week"/"last month" windows already covered above.
  'How many scans have I had year to date?',
  'Which product got the most scans in the last 30 days?',
  'Which product had the most scans this quarter?',
  'How did my scan activity trend last quarter?',
  'How many scans were flagged as suspicious this quarter?',

  // Data — certificates, third pass: a status filter paired with the product dimension
  // (ranking by product WITHIN a status, not a bare status count or a bare product
  // ranking), plus two certificateStatus values not exercised above (transfer_waiting_
  // confirmation, cancel_request, stolen-by-product).
  'Which product has the most active certificates?',
  'How many of my certificates are waiting for the buyer to confirm a transfer?',
  'Which product has the most certificates with a cancelled transfer request?',
  'Which product has the most stolen certificates reported?',

  // Data — certificates, third pass: explicit time windows on the created-at totals query.
  'How many certificates were created last quarter?',
  'How many certificates have I created so far this year?',

  // Data — owners, third pass: time windows on the created-at totals query, and
  // registeredOnly paired with a time window (existing registeredOnly question above has
  // no window at all).
  'How many new owners did I add last quarter?',
  'How many owners have I gained in the last 30 days?',
  'How many of my owners completed registration this year?',
  'How many owners registered this week?',

  // Data — leads, third pass: time windows on the totals query, and the product dimension
  // paired with a time window (existing product-dimension leads question above has none).
  'How many leads have I captured this quarter?',
  'How many leads did I get in the last 30 days?',
  'Which product brought in the most leads last quarter?',

  // Data — transfers, third pass: transferType paired with a dimension (gift ranked by
  // product/country, not just the bare gift-count totals question above), transferStatus
  // values not exercised above (failed, confirmation), and aggregate="avg" narrowed by
  // transferType (distinct from "which product holds its value best", which averages
  // across sell+gift+primary together).
  'Which product has the most gifted certificates?',
  'Which country buys the most gifted certificates?',
  'How many transfers failed this month?',
  'How many transfers are waiting on the recipient to confirm?',
  'Which product has the highest average sale price?',

  // Data — transfers, third pass: transferType + explicit time window with no dimension,
  // and the day dimension narrowed to a time window (existing day-dimension question above
  // has no window).
  'How many resales happened last month?',
  "What's my total resale revenue this year?",
  'Which day this month had the most transfers?',

  // Docs — certificates.md (audience: brand).
  'What do the different certificate statuses mean?',
  'What happens the first time someone scans a tag?',

  // Docs — products.md / assign-product-to-package.md.
  'How do I add a new product?',
  'How do I assign a product to a package of tags?',

  // Docs — scan-companion.md.
  'How do I set up a hardware scanner?',
  'How do I pair my phone to scan tags?',

  // Docs — settings-roles.md / settings-users-seats.md.
  'What roles can I assign to my team?',
  'How do seats work when I invite someone?',

  // Docs — tag-shop.md / tag-orders.md.
  'How do I customize a tag with my own logo?',
  'What do the tag order statuses mean?',

  // Docs — settings-features.md / settings-billing-plan.md.
  'How do I turn on white labeling for my certificates?',
  'How do I change my subscription plan?',

  // Docs — analytics.md.
  'What can I see on the Analytics page?',

  // Docs — home.md.
  "What's the setup checklist on my Home page for?",

  // Docs — people.md.
  "What's the difference between the Owners and Leads tabs?",

  // Docs — studio.md.
  "How do I make my certificates match my brand's look?",

  // Docs — settings-installation.md.
  'How do I install Vera on my own website?',

  // Docs — settings-integrations.md.
  'What tools can I connect to Vera?',

  // Docs — onboarding-auth.md.
  'What are the different ways I can sign in to Vera?',

  // Docs — settings-users.md.
  "How do I archive a team member who's left?",

  // Docs — settings-general.md.
  "How do I update my company's logo?",

  // Docs — settings-appearance.md.
  'Can I switch Vera to dark mode?',

  // Docs — settings-billing-overview.md.
  "Where can I see how many certificate activations I've used?",

  // Docs — product-updates.md.
  'Where can I see what has shipped in Vera recently?',

  // Docs — import-product-from-link.md.
  'Can I add a product by pasting a link to its page?',

  // Docs — sidebar-help-and-search.md.
  'How do I get help from inside Vera?',
]
